import { startCase } from 'lodash';
import moment from 'moment-timezone';

// Wording and phase logic for a workstation boot's progress. Everything here
// is a pure function of the boot (a WorkstationBoot or a plain object with the
// same camelCased fields) so the banner and dialog stay presentational.
//
// Attempt rows are only written when something happens, so what is being
// tried *now* is derived from the newest row.

export const PENDING_BOOT_STATES = [
  'pending',
  'started',
  'insufficient_capacity',
  'check',
];

const BOOT_STATE_LABELS = {
  pending: 'Queued',
  started: 'Starting',
  insufficient_capacity: 'Finding capacity',
  check: 'Checking',
  completed: 'Completed',
  failed: 'Failed',
};

const OUTCOME_LABELS = {
  insufficient_capacity: 'No capacity',
  switched_instance_type: 'Switched instance type',
  recreating: 'Recreating',
  error: 'Error',
  succeeded: 'Started',
  // The auto scaling group's own activity while it recreates the instance.
  terminating: 'Terminating old instance',
  terminated: 'Old instance terminated',
  launching: 'Launching instance',
  launched: 'Instance launched',
  activity: 'Group activity',
};

const PHASE_LABELS = {
  start_instance: 'Start',
  asg_recreate: 'Recreate',
};

const PHASE_DESCRIPTIONS = {
  start_instance: 'Start the instance',
  asg_recreate: 'Recreate the instance in another zone',
};

// What the boot is doing, for the dialog's state chip.
const BOOT_PHASE_LABELS = {
  starting: 'Starting',
  finding_capacity: 'Finding capacity',
  recreating: 'Recreating',
  completed: 'Completed',
  failed: 'Failed',
};

const FINDING_CAPACITY = 'Finding capacity for your workstation';
const OTHER_ZONES = 'Looking in other availability zones';

const label = (labels, value) => {
  if (value === null || value === undefined || value === '') return 'Unknown';
  return labels[value] || startCase(value);
};

export const bootStateLabel = state => label(BOOT_STATE_LABELS, state);
export const outcomeLabel = outcome => label(OUTCOME_LABELS, outcome);
export const phaseLabel = phase => label(PHASE_LABELS, phase);
export const phaseDescription = phase => label(PHASE_DESCRIPTIONS, phase);

// Whether the boot's service reports its attempts. Some accounts' boot
// services report nothing during a capacity search, so their progress would
// read "attempt 1" for ever; they get no progress UI. An older API without
// the flag counts as reporting.
export const bootReportsAttempts = boot =>
  !boot || boot.reportsAttempts !== false;

export const isBootPending = boot =>
  Boolean(boot) && PENDING_BOOT_STATES.includes(boot.state);

export const bootAttempts = boot => (boot && boot.capacityAttempts) || [];

const isNumber = v => typeof v === 'number' && !Number.isNaN(v);

// "45s", "3m 12s", "1h 02m".
export const formatElapsed = ms => {
  const total = Math.max(0, Math.floor((ms || 0) / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  if (minutes > 0) return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
  return `${seconds}s`;
};

export const formatAttemptTime = value =>
  value
    ? moment
        .utc(value)
        .local()
        .format('MMM D, HH:mm:ss')
    : '';

// "3 of 5", "3" when the maximum is unknown, '' when the number is unknown.
export const attemptCount = (attemptNumber, maxAttempts) => {
  if (!isNumber(attemptNumber)) return '';
  return isNumber(maxAttempts)
    ? `${attemptNumber} of ${maxAttempts}`
    : `${attemptNumber}`;
};

const tryingLine = (instanceType, attemptNumber, maxAttempts) => {
  const count = attemptCount(attemptNumber, maxAttempts);
  return [
    instanceType ? `Trying ${instanceType}` : 'Trying again',
    count && `attempt ${count}`,
  ]
    .filter(Boolean)
    .join(' · ');
};

const isExhausted = attempt =>
  attempt.outcome === 'insufficient_capacity' &&
  attempt.phase !== 'asg_recreate' &&
  isNumber(attempt.attemptNumber) &&
  isNumber(attempt.maxAttempts) &&
  attempt.attemptNumber >= attempt.maxAttempts;

const isAsgRow = attempt => attempt.phase === 'asg_recreate';
const isStartRow = attempt => !isAsgRow(attempt);
const isIce = attempt => attempt.outcome === 'insufficient_capacity';

const plannedTypes = boot =>
  (boot && Array.isArray(boot.plannedInstanceTypes)
    ? boot.plannedInstanceTypes
    : []
  ).filter(Boolean);

// The planned type after this one, when the plan says.
const nextPlannedType = (boot, type) => {
  const planned = plannedTypes(boot);
  const index = planned.indexOf(type);
  return index === -1 ? null : planned[index + 1] || null;
};

// Requested but not yet picked up. The state can stay 'pending' while the
// search runs, so a phase past 'starting' means it is under way.
const isQueued = boot =>
  boot.state === 'pending' && (!boot.phase || boot.phase === 'starting');

// The type tried first: the plan's, else the workstation's own type.
const firstType = (boot, workstation) =>
  plannedTypes(boot)[0] ||
  (workstation &&
    workstation.instanceType &&
    workstation.instanceType.identifier) ||
  null;

// A start row with no type comes from an older boot service that reports
// neither the type nor the attempt numbers; it belongs to the usual machine,
// the first type tried. Copied field by field, as a model doesn't spread.
const typedAttempts = (boot, workstation) => {
  const first = firstType(boot, workstation);
  return bootAttempts(boot).map(row =>
    isStartRow(row) && !row.instanceTypeIdentifier && first
      ? {
          id: row.id,
          phase: row.phase,
          outcome: row.outcome,
          instanceTypeIdentifier: first,
          availabilityZone: row.availabilityZone,
          attemptNumber: row.attemptNumber,
          maxAttempts: row.maxAttempts,
          message: row.message,
          createdAt: row.createdAt,
          legacy: true,
        }
      : row,
  );
};

// Whether rows that name the type say it has run out (a legacy row alone
// never exhausts a type: it carries no attempt numbers).
const exhaustedByTypedRows = (attempts, type) =>
  attempts.some(
    a =>
      !a.legacy &&
      isStartRow(a) &&
      a.instanceTypeIdentifier === type &&
      ((isIce(a) && isExhausted(a)) || a.outcome === 'error'),
  );

const plannedAttempts = (boot, attempt) =>
  isNumber(attempt.maxAttempts)
    ? attempt.maxAttempts
    : isNumber(boot.attemptsPerType)
    ? boot.attemptsPerType
    : null;

// The type the start phase is trying now, or null when it isn't trying one:
// the group has the instance, it started, or the boot is still queued.
const currentStartType = (boot, workstation) => {
  const attempts = typedAttempts(boot, workstation);
  if (attempts.some(a => isAsgRow(a) || a.outcome === 'succeeded')) {
    return null;
  }
  const last = lastWhere(attempts, isStartRow);
  if (!last) {
    return isQueued(boot) ? null : firstType(boot, workstation);
  }
  if (last.legacy) {
    // Still on the usual machine, unless typed rows say it ran out.
    const types = plannedTypes(boot).length
      ? plannedTypes(boot)
      : [firstType(boot, workstation)];
    return types.find(t => !exhaustedByTypedRows(attempts, t)) || null;
  }
  const type = last.instanceTypeIdentifier;
  if ((isIce(last) && isExhausted(last)) || last.outcome === 'error') {
    return nextPlannedType(boot, type);
  }
  return type || null;
};

// One group per planned type, in the order they are tried, with a dot per
// attempt: 'miss' for each reported no-capacity attempt, 'trying' for the
// one under way, 'success' for the one it started on.
// → [{ type, state: started|current|exhausted|waiting, dots, tooltip }]
export const bootPlan = (boot, workstation) => {
  if (!boot || boot.forceRebuild) return [];
  const attempts = typedAttempts(boot, workstation);
  const startRows = attempts.filter(isStartRow);
  // Without a plan, the workstation's type is the one group when the boot
  // says how many attempts it gets.
  const types = plannedTypes(boot).length
    ? [...plannedTypes(boot)]
    : [isNumber(boot.attemptsPerType) && firstType(boot, workstation)].filter(
        Boolean,
      );
  startRows.forEach(a => {
    const type = a.instanceTypeIdentifier;
    if (type && !types.includes(type)) types.push(type);
  });
  const current = isBootPending(boot)
    ? currentStartType(boot, workstation)
    : null;

  return types.map(type => {
    const rows = startRows.filter(a => a.instanceTypeIdentifier === type);
    // Rows can be missing (or legacy), so a reported attempt number counts
    // for the attempts before it too.
    const ices = rows.filter(isIce);
    const misses = Math.max(
      ices.length,
      ...ices.map(a => (isNumber(a.attemptNumber) ? a.attemptNumber : 0)),
    );
    const withMax = lastWhere(rows, a => isNumber(a.maxAttempts));
    const total = withMax
      ? withMax.maxAttempts
      : isNumber(boot.attemptsPerType)
      ? boot.attemptsPerType
      : null;
    const started = attempts.some(
      a => a.outcome === 'succeeded' && a.instanceTypeIdentifier === type,
    );
    // Tried and moved past counts as exhausted too.
    const exhausted =
      (misses > 0 && type !== current) ||
      rows.some(a => isIce(a) && isExhausted(a)) ||
      rows.some(a => a.outcome === 'error') ||
      (isNumber(total) && misses >= total);

    let state = 'waiting';
    if (started) state = 'started';
    else if (type === current) state = 'current';
    else if (exhausted) state = 'exhausted';

    const count = Math.max(isNumber(total) ? total : 0, misses);
    const dots = Array.from({ length: count }, (_, i) => {
      if (i < misses) return 'miss';
      if (i === misses && state === 'current') return 'trying';
      if (i === misses && state === 'started') return 'success';
      return 'empty';
    });
    if (state === 'started' && misses >= count) dots.push('success');

    const tried = Math.min(misses + (state === 'current' ? 1 : 0), count);
    const tooltip = {
      started: 'Started on this type',
      current: `Trying now · attempt ${attemptCount(
        tried || 1,
        isNumber(total) ? total : null,
      )}`,
      exhausted: `No capacity after ${attemptsLabel(misses || count)}`,
      waiting: 'Not tried yet',
    }[state];

    return { type, state, dots, tooltip };
  });
};

const noCapacityInZone = attempt => {
  const type = attempt.instanceTypeIdentifier;
  const zone = attempt.availabilityZone;
  if (!type && !zone) return null;
  return ['No capacity', type && `for ${type}`, zone && `in ${zone}`]
    .filter(Boolean)
    .join(' ');
};

const lastWhere = (attempts, predicate) =>
  [...attempts].reverse().find(predicate) || null;

const attemptsLabel = n => `${n} ${n === 1 ? 'attempt' : 'attempts'}`;

const startingTitle = boot =>
  boot.forceRebuild
    ? 'Rebuilding your workstation'
    : 'Starting your workstation';

const inZone = (text, zone) => (zone ? `${text} in ${zone}` : text);

// The auto scaling group recreating the instance: after a capacity search
// ran out of types, or straight away for a rebuild. Rows mirror the group's
// activity log, so the newest one says what it is doing.
const recreateCopy = (boot, attempts, last) => {
  const title = boot.forceRebuild ? startingTitle(boot) : OTHER_ZONES;
  const zone = last.availabilityZone;
  const line = text => ({ title, line: text, secondaryLine: null });

  if (last.phase === 'asg_recreate') {
    switch (last.outcome) {
      case 'terminating':
        return line('Terminating the old instance');
      case 'terminated':
        return line('Old instance terminated, launching a new instance');
      case 'launching':
        return line(inZone('Launching a new instance', zone));
      case 'launched':
        // Setup still runs until the boot posts its succeeded row.
        return line(`${inZone('Instance launched', zone)}, finishing setup`);
      case 'error':
        return line(inZone('Launch failed', zone));
      default:
        break;
    }
  }

  const zoneMiss = lastWhere(
    attempts,
    a => a.phase === 'asg_recreate' && a.outcome === 'insufficient_capacity',
  );
  return {
    title,
    line: boot.forceRebuild
      ? 'Recreating the instance'
      : 'Recreating the instance where capacity is available',
    secondaryLine: zoneMiss ? noCapacityInZone(zoneMiss) : null,
  };
};

// Title and lines for a boot that is still under way.
const pendingCopy = (boot, workstation) => {
  const attempts = typedAttempts(boot, workstation);
  const last = attempts[attempts.length - 1];

  if (!last) {
    if (isQueued(boot)) {
      return { title: startingTitle(boot), line: 'Boot requested' };
    }
    if (boot.state === 'insufficient_capacity') {
      return { title: FINDING_CAPACITY, line: 'Waiting for capacity' };
    }
    if (boot.forceRebuild) {
      return { title: startingTitle(boot), line: 'Recreating the instance' };
    }
    const first = firstType(boot, workstation);
    return {
      title: startingTitle(boot),
      line: first
        ? tryingLine(first, 1, boot.attemptsPerType)
        : 'Starting the existing instance',
    };
  }

  if (last.outcome === 'succeeded') {
    const started = last.instanceTypeIdentifier;
    const requested = workstation?.instanceType?.identifier;
    return {
      title: started ? `Started on ${started}` : 'Instance started',
      line: 'Finishing setup, your workstation will be ready shortly',
      secondaryLine:
        started && requested && started !== requested
          ? `Temporarily running as ${started} due to limited capacity — requested ${requested}`
          : null,
    };
  }

  if (
    last.outcome === 'recreating' ||
    attempts.some(a => a.phase === 'asg_recreate')
  ) {
    return recreateCopy(boot, attempts, last);
  }

  // AWS's own messages stay in the admin dialog; users get our words.
  if (last.outcome === 'error') {
    const type = last.instanceTypeIdentifier;
    // A legacy error doesn't move the search on to another type.
    const next = last.legacy ? null : nextPlannedType(boot, type);
    return {
      title: FINDING_CAPACITY,
      line: [
        type ? `Something went wrong with ${type}` : 'Something went wrong',
        next ? `trying ${next} next` : 'retrying',
      ].join(' · '),
    };
  }

  if (isIce(last)) {
    const type = last.instanceTypeIdentifier;
    if (isExhausted(last)) {
      const next = nextPlannedType(boot, type);
      return {
        title: FINDING_CAPACITY,
        line: `No capacity for ${type || 'this instance type'} · ${
          next ? `trying ${next} next` : 'trying the next instance type'
        }`,
      };
    }
    // A legacy row has no attempt number: count the misses on the type.
    const missed = attempts.filter(
      a => isStartRow(a) && isIce(a) && a.instanceTypeIdentifier === type,
    ).length;
    const max = plannedAttempts(boot, last);
    const counted = isNumber(max) ? Math.min(missed + 1, max) : missed + 1;
    const next = isNumber(last.attemptNumber)
      ? last.attemptNumber + 1
      : last.legacy
      ? counted
      : null;
    return {
      title: FINDING_CAPACITY,
      line: tryingLine(type, next, max),
    };
  }

  if (last.outcome === 'switched_instance_type') {
    return {
      title: FINDING_CAPACITY,
      line: tryingLine(
        last.instanceTypeIdentifier,
        1,
        plannedAttempts(boot, last),
      ),
    };
  }

  return { title: FINDING_CAPACITY, line: outcomeLabel(last.outcome) };
};

const failedLine = boot => {
  const attempts = bootAttempts(boot);
  const last = attempts[attempts.length - 1];
  if (last && isIce(last)) {
    return 'Capacity was not available for any enabled instance type';
  }
  return 'The boot failed';
};

const elapsedSince = (createdAt, now) =>
  createdAt ? formatElapsed(now - moment.utc(createdAt).valueOf()) : null;

// What the boot banner shows, or null for no banner.
// → { severity, title, line, secondaryLine, plan, elapsed, pending }
export const bootBanner = (boot, workstation, now = Date.now()) => {
  if (!boot) return null;

  if (boot.state === 'failed') {
    return {
      severity: 'error',
      title: 'Your workstation could not be started',
      line: failedLine(boot),
      secondaryLine: null,
      plan: bootPlan(boot, workstation),
      elapsed: null,
      pending: false,
    };
  }

  if (!isBootPending(boot)) return null;

  const { title, line, secondaryLine = null } = pendingCopy(boot, workstation);
  return {
    severity: 'info',
    title,
    line,
    secondaryLine,
    plan: bootPlan(boot, workstation),
    elapsed: elapsedSince(boot.createdAt, now),
    pending: true,
  };
};

const requestedAt = boot =>
  moment
    .utc(boot.createdAt)
    .local()
    .format('MMM D, HH:mm');

// When a finished boot stopped: its newest attempt, else its last update.
const finishedAt = boot => {
  const attempts = bootAttempts(boot);
  const last = attempts[attempts.length - 1];
  const at = (last && last.createdAt) || boot.updatedAt;
  return at ? moment.utc(at).valueOf() : null;
};

// "Requested Oct 6, 14:02 · 7 attempts · elapsed 4m 10s" while pending,
// "… · took 4m 10s" once finished.
export const bootSummary = (boot, now = Date.now()) => {
  if (!boot) return '';
  const pending = isBootPending(boot);
  const end = pending ? now : finishedAt(boot);
  const duration =
    boot.createdAt && end ? elapsedSince(boot.createdAt, end) : null;
  return [
    boot.createdAt && `Requested ${requestedAt(boot)}`,
    attemptsLabel(bootAttempts(boot).length),
    duration && `${pending ? 'elapsed' : 'took'} ${duration}`,
  ]
    .filter(Boolean)
    .join(' · ');
};

// What the boot is doing, from its phase: "Setting up" once an instance is up
// but the boot hasn't completed.
export const bootPhaseLabel = boot => {
  if (!boot) return 'Unknown';
  if (boot.state === 'completed' || boot.state === 'failed') {
    return bootStateLabel(boot.state);
  }
  const attempts = bootAttempts(boot);
  const last = attempts[attempts.length - 1];
  if (last && (last.outcome === 'launched' || last.outcome === 'succeeded')) {
    return 'Setting up';
  }
  if (last && isAsgRow(last)) return BOOT_PHASE_LABELS.recreating;
  if (boot.phase && BOOT_PHASE_LABELS[boot.phase]) {
    return BOOT_PHASE_LABELS[boot.phase];
  }
  return bootStateLabel(boot.state);
};

// "0:24", "18:30": the connect dialog's clock.
export const formatClock = ms => {
  const total = Math.max(0, Math.floor((ms || 0) / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

// A rebuild's post-deployment steps take about this long.
export const SETUP_DURATION_MS = 10 * 60 * 1000;

// Time-based progress that never completes on its own: linear to 90%, then
// easing towards 95% however long it overruns.
export const setupProgress = elapsedMs => {
  const p = Math.max(0, elapsedMs) / SETUP_DURATION_MS;
  if (p <= 0.9) return p;
  return 0.95 - 0.05 * Math.exp(-(p - 0.9) * 10);
};

const at = value => (value ? moment.utc(value).valueOf() : null);

const REQUESTING = { active: 1, done: [] };
const STARTING_UP = { active: 2, done: [1] };
const CONNECTING = { active: 3, done: [1, 2] };

// The type the workstation is running on: the one the boot started on, else
// the provisioned type, else the usual one. Works without a boot too.
const landedType = (boot, workstation) => {
  const succeeded = boot
    ? lastWhere(
        typedAttempts(boot, workstation),
        a => a.outcome === 'succeeded',
      )
    : null;
  return (
    (succeeded && succeeded.instanceTypeIdentifier) ||
    (workstation &&
      workstation.actualInstanceType &&
      workstation.actualInstanceType.identifier) ||
    firstType(boot, workstation)
  );
};

// "Running on {type}", and which fallback it is when it isn't the usual
// type. Not "smaller": a fallback can be bigger than the usual machine.
const landedCaption = (boot, workstation) => {
  const type = landedType(boot, workstation);
  const planned = plannedTypes(boot);
  const usual = planned[0] || firstType(boot, workstation);
  const notUsual = Boolean(type && usual && type !== usual);
  const position = planned.indexOf(type);
  return {
    type,
    text: type ? 'Running on' : null,
    right: !notUsual
      ? null
      : position > 0
      ? `Fallback ${position} of ${planned.length - 1}`
      : 'Not the usual type',
    rightTone: notUsual ? 'warning' : null,
  };
};

// What the connect dialog shows while it waits for the workstation:
// → { state, stepper: { active, done }, headline, spinner, elapsed,
//     bar: { kind: 'segments', segments: [{ type, state, fill }] }
//        | { kind: 'indeterminate', speed: 'normal' | 'slow' }
//        | { kind: 'determinate', value },
//     caption: { type, text, right, rightTone } }
// or null without a boot to go on. `startedAt` is when this connect began;
// the clock runs from it whatever the state.
export const connectStatus = (workstation, { now = Date.now(), startedAt }) => {
  const boot = workstation && workstation.lastBoot;
  if (!boot || !bootReportsAttempts(boot)) return null;

  const attempts = typedAttempts(boot, workstation);
  const elapsed = formatClock(now - (startedAt || now));
  const base = { elapsed, spinner: true, stepper: REQUESTING };
  const runningType = () => landedType(boot, workstation);

  if (boot.state === 'failed') {
    const failed = bootBanner(boot, workstation, now);
    return {
      ...base,
      state: 'failed',
      spinner: false,
      headline: failed.title,
      bar: null,
      caption: { type: null, text: failed.line, right: null },
    };
  }

  // The rebuild path: the group recreates the instance, then the
  // post-deployment steps run until the boot completes. A rebuild goes
  // there straight away.
  const asgRows = attempts.filter(
    a => isAsgRow(a) || a.outcome === 'recreating',
  );
  if ((asgRows.length > 0 || boot.forceRebuild) && boot.state !== 'completed') {
    const launched = lastWhere(
      attempts,
      a => isAsgRow(a) && a.outcome === 'launched',
    );
    if (launched) {
      const setupMs = now - at(launched.createdAt);
      const type = runningType();
      return {
        ...base,
        state: 'setup',
        stepper: STARTING_UP,
        headline: 'Completing workstation setup',
        bar: { kind: 'determinate', value: setupProgress(setupMs) },
        caption: {
          type,
          text: type ? 'Running on' : null,
          right:
            setupMs >= SETUP_DURATION_MS
              ? 'Almost done'
              : `About ${Math.max(
                  1,
                  Math.ceil((SETUP_DURATION_MS - setupMs) / 60000),
                )} min left`,
        },
      };
    }
    const recreating =
      lastWhere(attempts, a => a.outcome === 'recreating') ||
      asgRows[0] ||
      boot;
    return {
      ...base,
      state: 'rebuilding',
      headline: 'Rebuilding your workstation',
      bar: { kind: 'indeterminate', speed: 'slow' },
      caption: {
        type: null,
        text: 'Any instance type, any zone · this can take a long time',
        right: `Rebuild started ${formatClock(
          now - at(recreating.createdAt),
        )} ago`,
      },
    };
  }

  // Started (or completed): waiting for Windows and DCV to come up.
  if (
    boot.state === 'completed' ||
    attempts.some(a => a.outcome === 'succeeded')
  ) {
    return {
      ...base,
      state: 'booting',
      stepper: STARTING_UP,
      headline: 'Waiting for the desktop',
      bar: { kind: 'indeterminate', speed: 'normal' },
      caption: landedCaption(boot, workstation),
    };
  }

  // Searching: one segment per type, primary first.
  const plan = bootPlan(boot, workstation);
  const current = isQueued(boot)
    ? firstType(boot, workstation)
    : currentStartType(boot, workstation);
  const types = plan.length
    ? plan.map(group => group.type)
    : [current].filter(Boolean);
  if (types.length === 0) {
    // No plan and no type to name.
    return {
      ...base,
      state: 'primary',
      headline: 'Requesting a machine from AWS',
      bar: { kind: 'indeterminate', speed: 'normal' },
      caption: { type: null, text: null, right: null },
    };
  }

  const index = types.indexOf(current);
  // The current type's attempts so far and how many it gets, if known.
  const group = plan.find(g => g.type === current);
  const misses = group ? group.dots.filter(d => d === 'miss').length : 0;
  const lastRow = lastWhere(
    attempts,
    a => isStartRow(a) && a.instanceTypeIdentifier === current,
  );
  const max = lastRow
    ? plannedAttempts(boot, lastRow)
    : isNumber(boot.attemptsPerType)
    ? boot.attemptsPerType
    : null;

  const segments = types.map((type, i) => {
    if (index === -1 || i < index) {
      return { type, state: 'exhausted', fill: 1 };
    }
    if (i === index) {
      // Without a known maximum there is nothing to fill against.
      return {
        type,
        state: 'active',
        fill: isNumber(max) && max > 0 ? Math.min(misses / max, 1) : 0,
      };
    }
    return { type, state: 'pending', fill: 0 };
  });

  if (index === -1) {
    // Every type tried and the group hasn't taken over yet.
    return {
      ...base,
      state: 'fallback',
      headline: 'Expanding search',
      bar: { kind: 'segments', segments },
      caption: {
        type: null,
        text: 'All fallbacks tried · moving to rebuild',
        right: null,
      },
    };
  }

  const attempt = isNumber(max) ? Math.min(misses + 1, max) : misses + 1;

  return {
    ...base,
    state: index === 0 ? 'primary' : 'fallback',
    headline:
      index === 0 ? 'Requesting a machine from AWS' : 'Expanding search',
    bar: { kind: 'segments', segments },
    caption: {
      type: current,
      text: 'Trying',
      attempt: `attempt ${attemptCount(attempt, max)}`,
      right:
        index === 0
          ? 'Usual machine'
          : `Fallback ${index} of ${types.length - 1}`,
    },
  };
};

const isRunning = workstation =>
  typeof workstation.isRunning === 'function'
    ? workstation.isRunning()
    : workstation.instanceState === 'running';

// Step 3, once the desktop is up and the client is being opened: the same
// block as the boot's steps, with the clock carrying on. `error` replaces it
// with the failure, in our words.
export const connectStepStatus = (
  workstation,
  { now = Date.now(), startedAt, protocol, error },
) => {
  const elapsed = formatClock(now - (startedAt || now));
  if (error) {
    return {
      state: 'connect_failed',
      stepper: CONNECTING,
      elapsed,
      spinner: false,
      headline: 'Could not connect to the desktop',
      bar: null,
      caption: { type: null, text: error, right: null },
    };
  }
  return {
    state: 'connecting',
    stepper: CONNECTING,
    elapsed,
    spinner: true,
    headline:
      protocol === 'parsec' ? 'Launching Parsec' : 'Connecting to the desktop',
    bar: { kind: 'indeterminate', speed: 'normal' },
    caption: landedCaption(workstation && workstation.lastBoot, workstation),
  };
};

// What the workstation page's banner shows: connectStatus() timed from the
// boot itself, or null when there is nothing to show. It goes the moment the
// boot completes (waiting for the desktop belongs to the connect dialog); a
// failed boot stays until the workstation runs anyway.
export const pageStatus = (workstation, { now = Date.now() } = {}) => {
  const boot = workstation && workstation.lastBoot;
  if (!boot || !bootReportsAttempts(boot)) return null;
  const failed = boot.state === 'failed';
  if (failed ? isRunning(workstation) : !isBootPending(boot)) return null;
  return connectStatus(workstation, { now, startedAt: at(boot.createdAt) });
};

// One boot in the dialog's picker: "Oct 6, 14:02 · Completed · 7 attempts",
// with markers for a rebuild and a scheduled boot.
export const bootEntry = boot => ({
  primary: [
    boot.createdAt && requestedAt(boot),
    bootPhaseLabel(boot),
    attemptsLabel(bootAttempts(boot).length),
  ]
    .filter(Boolean)
    .join(' · '),
  markers: [
    boot.forceRebuild && 'Rebuild',
    boot.bootScheduleRunId && 'Scheduled',
  ].filter(Boolean),
});

// The AWS console page for a step function execution; the region is segment
// 3 of the ARN (arn:aws:states:<region>:<account>:execution:...).
export const stepFunctionUrl = executionArn => {
  if (!executionArn) return null;
  const region = executionArn.split(':')[3];
  if (!region) return null;
  return `https://${region}.console.aws.amazon.com/states/home?region=${region}#/executions/details/${executionArn}`;
};
